import type { FC, ReactNode } from 'react'
import { createElement, useCallback, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { Notes, PowerSettingsNew, Reply as ReplyIcon } from '@mui/icons-material'
import type {
  ClaudeSession,
  Command,
  ConversationTurn,
  ConversationTurnEntry,
  ErrorRecoveryType,
} from '@/features/events/conversation/state/types'
import { isNotInteractable } from '@/features/events/conversation/state/types'
import { EventType } from '@/features/events/shared/types'
import type { UUID } from '@/common/types'
import { isClaudeApiError } from '@/features/events/conversation/models'
import { isExitPlanModeToolInput } from '@/features/events/shared/types/tool-inputs'
import {
  deriveAgentModeState,
  getPreviousConversationTurn,
} from '@/features/events/shared/utils/agentModeUtils'
import { ToastNotification, standardTooltipProps } from '@/common/components'
import { EventDetailsDialog, UserInputDialog } from '.'
import { SessionDialog } from './SessionDialog'
import { AgentModeBadgeTransition } from '../agent-mode-badges'
import {
  ExpandControl,
  expandControlIconButtonSx,
  useExpandDialog,
  useCommandSubmitter,
  useUserCommandDialogs,
  useZoomLevel,
  ZoomControls,
} from '../controls'
import { useDialog } from '@/common/hooks'
import {
  CollectUserQuestionsBubble,
  ErroredEventBubble,
  EventBubble,
  FileModificationBubble,
  NotificationBubble,
} from '../bubbles'
import { useConversationNotifications } from '../timeline'
import {
  selectPlanApprovalContextRenderer,
  selectToolPermissionContextRenderer,
} from '../utils/contextRendererSelectors'
import { ERRORED_TOOL_NAME } from '@/infra/sse/conversation/decryption/resolveNotificationContext'

interface MostRecentEventDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly conversationTurn: ConversationTurn | null
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
  readonly onOpenConversation?: () => void
  readonly onResumeSession?: (() => void) | undefined
  readonly isTransitioning?: boolean | undefined
}

interface EventContentProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly conversationTurn: ConversationTurn | null
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect?: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

export const MostRecentEventDialog: FC<MostRecentEventDialogProps> = ({
  open,
  onClose,
  event,
  session,
  latestEvent,
  conversationTurn,
  recordCommand,
  createNewTurn,
  onOpenConversation,
  onResumeSession,
  isTransitioning = false,
}) => {
  const theme = useTheme()
  const eventDetailsDialog = useDialog()
  const { notification, showError, close: closeNotification } = useConversationNotifications()

  const submitter = useCommandSubmitter({
    session,
    conversationTurn,
    recordCommand,
    createNewTurn,
    onSuccess: onClose,
    onError: (error: Error) => {
      showError(error.message || 'Failed to submit user option')
    },
  })

  const previousTurn = useMemo(() => {
    if (!conversationTurn) return null
    return getPreviousConversationTurn(session, conversationTurn.userPromptId)
  }, [session, conversationTurn])

  const agentModeState = useMemo(
    () => deriveAgentModeState(conversationTurn, previousTurn, session.currentAgentMode),
    [conversationTurn, previousTurn, session.currentAgentMode],
  )

  const currentAgentMode = agentModeState.currentMode

  const agentModeBadgeComponent = useMemo((): ReactNode | undefined => {
    if (agentModeState.pendingTransition) {
      return createElement(AgentModeBadgeTransition, {
        from: agentModeState.pendingTransition.from,
        to: agentModeState.pendingTransition.to,
      })
    }
    return undefined
  }, [agentModeState.pendingTransition])

  const dialogs = useUserCommandDialogs({
    session,
    conversationTurn,
    submitter,
    currentAgentMode,
  })

  const handleCloseNotification = useCallback(() => {
    closeNotification()
    submitter.clearError()
  }, [closeNotification, submitter])

  const title = resolveDialogTitle(event.eventType)

  const handleOpenEventDetails = useCallback(() => {
    eventDetailsDialog.open()
  }, [eventDetailsDialog])

  const { isExpanded, handleToggleExpand } = useExpandDialog(open)
  const { zoomLevel, fontSize, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel } =
    useZoomLevel(open)

  const windowControlSlot2 = useMemo(() => {
    if (!onOpenConversation) return null

    return (
      <Tooltip {...standardTooltipProps} title="View full conversation">
        <IconButton onClick={onOpenConversation} size="small" sx={expandControlIconButtonSx}>
          <Notes fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  }, [onOpenConversation])

  const windowControlSlot3 = useMemo(
    () => <ExpandControl isExpanded={isExpanded} onToggle={handleToggleExpand} />,
    [isExpanded, handleToggleExpand],
  )

  const controlsRowLeft = useMemo(
    () => (
      <ZoomControls
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        showZoomLevel={showZoomLevel}
      />
    ),
    [zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel],
  )

  const controlsRowCenter = useMemo(() => {
    if (isNotInteractable(session)) return null

    if (onResumeSession) {
      return (
        <Tooltip
          {...standardTooltipProps}
          title={isTransitioning ? 'Resuming...' : 'Resume session'}
        >
          <span>
            <IconButton
              onClick={onResumeSession}
              sx={resumeButtonSx}
              disabled={isTransitioning}
              aria-label={isTransitioning ? 'Session is resuming' : 'Resume session'}
            >
              <PowerSettingsNew sx={resumeIconSx(theme)} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }

    if (event.eventType !== EventType.AGENT_REPLY) return null
    if (isClaudeApiError(event.agentResponse)) return null

    return (
      <Tooltip {...standardTooltipProps} title="Reply to Agent">
        <IconButton onClick={dialogs.reply.open} sx={replyButtonSx}>
          <ReplyIcon sx={replyIconSx(theme)} />
        </IconButton>
      </Tooltip>
    )
  }, [
    session,
    event.eventType,
    event.agentResponse,
    dialogs.reply.open,
    theme,
    onResumeSession,
    isTransitioning,
  ])

  return (
    <>
      <SessionDialog
        open={open}
        onClose={onClose}
        title={title}
        breadcrumb={session.sessionBreadcrumb}
        agentMode={currentAgentMode}
        agentModeBadge={agentModeBadgeComponent}
        dangerouslySkipPermissions={session.dangerouslySkipPermissions}
        autoModeSupported={session.autoModeSupported}
        windowControlSlot2={windowControlSlot2}
        windowControlSlot3={windowControlSlot3}
        controlsRowLeft={controlsRowLeft}
        controlsRowCenter={controlsRowCenter}
        isExpanded={isExpanded}
        fontSize={fontSize}
      >
        <EventContent
          event={event}
          session={session}
          latestEvent={latestEvent}
          conversationTurn={conversationTurn}
          recordCommand={recordCommand}
          submitErrorRecovery={submitter.submitErrorRecovery}
          {...(!isNotInteractable(session) && { onOptionSelect: dialogs.userOption.handleSelect })}
          onEventDetailsClick={handleOpenEventDetails}
        />
      </SessionDialog>

      <EventDetailsDialog
        open={eventDetailsDialog.isOpen}
        onClose={eventDetailsDialog.close}
        event={event}
        session={session}
      />

      <ToastNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
        autoHideDuration={notification.severity === 'error' ? 6000 : 4000}
      />

      {dialogs.userOption.selectedOption && dialogs.userOption.selectedEvent && (
        <UserInputDialog
          open={dialogs.userOption.isOpen}
          onClose={dialogs.userOption.close}
          onSubmit={dialogs.userOption.handleSubmit}
          session={session}
          isSubmitting={submitter.isSubmitting}
          error={submitter.error}
          draftType="custom_option"
          refEventId={dialogs.userOption.selectedEvent.eventId}
          title="Custom Response"
          placeholder="Explain what you want Claude Code to do differently..."
          description={dialogs.userOption.selectedOption.option}
          currentAgentMode={currentAgentMode}
          dangerouslySkipPermissions={session.dangerouslySkipPermissions}
          autoModeSupported={session.autoModeSupported}
        />
      )}

      {conversationTurn && (
        <UserInputDialog
          open={dialogs.reply.isOpen}
          onClose={dialogs.reply.close}
          onSubmit={dialogs.reply.handleSubmit}
          session={session}
          isSubmitting={submitter.isSubmitting}
          error={submitter.error}
          draftType="user_reply"
          refEventId={conversationTurn.userPromptId}
          title="Reply to Agent"
          placeholder="Enter your reply to Claude Code..."
          description="Reply to Claude Code's response"
          currentAgentMode={currentAgentMode}
          dangerouslySkipPermissions={session.dangerouslySkipPermissions}
          autoModeSupported={session.autoModeSupported}
          submitButtonLabel="Reply"
          submitButtonLoadingLabel="Replying..."
        />
      )}
    </>
  )
}

const replyButtonSx: SxProps<Theme> = {
  padding: '4px',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
}

const replyIconSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: '1.5rem',
  color: theme.palette.success.main,
  opacity: 0.7,
})

const resumeButtonSx: SxProps<Theme> = {
  padding: '4px',
  color: 'success.main',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    transform: 'scale(1.35)',
  },
  '&.Mui-disabled': {
    color: 'success.main',
    opacity: 0.6,
  },
}

const resumeIconSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: '1.75rem',
  color: theme.palette.success.main,
  opacity: 0.7,
})

interface CommonBubbleProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly onOptionSelect?: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

const EventContent: FC<EventContentProps> = ({
  event,
  session,
  latestEvent,
  conversationTurn,
  recordCommand,
  submitErrorRecovery,
  onOptionSelect,
  onEventDetailsClick,
}) => {
  const allUserCommands = conversationTurn?.userCommands ?? []

  const commonBubbleProps: CommonBubbleProps = {
    event,
    session,
    latestEvent,
    allUserCommands,
    ...(onOptionSelect && { onOptionSelect }),
    ...(onEventDetailsClick && { onEventDetailsClick }),
  }

  if (event.eventType === EventType.FILE_MODIFICATION) {
    return (
      <FileModificationBubble event={event} {...(onEventDetailsClick && { onEventDetailsClick })} />
    )
  }

  const planApprovalContent = renderPlanApprovalContent(event, commonBubbleProps)
  if (planApprovalContent) return planApprovalContent

  const toolPermissionContent = renderToolPermissionContent(event, commonBubbleProps, session)
  if (toolPermissionContent) return toolPermissionContent

  if (event.eventType === EventType.AGENT_QUESTIONS) {
    return (
      <CollectUserQuestionsBubble
        event={event}
        session={session}
        conversationTurn={conversationTurn}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        recordCommand={recordCommand}
        {...(onEventDetailsClick && { onEventDetailsClick })}
      />
    )
  }

  if (event.eventType === EventType.NON_INTERACTIVE_NOTIFICATION) {
    return (
      <NotificationBubble {...commonBubbleProps}>
        <Typography>{event.notificationMessage}</Typography>
      </NotificationBubble>
    )
  }

  const isErrorEvent =
    event.eventType === EventType.ERRORED_EVENT ||
    (event.eventType === EventType.AGENT_REPLY && isClaudeApiError(event.agentResponse))

  if (isErrorEvent) {
    return (
      <ErroredEventBubble
        event={event}
        session={session}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        onOptionSelect={onOptionSelect}
        onEventDetailsClick={onEventDetailsClick}
        submitErrorRecovery={submitErrorRecovery}
      />
    )
  }

  return (
    <EventBubble
      event={event}
      isAgent={true}
      session={session}
      latestEvent={latestEvent}
      allUserCommands={allUserCommands}
      {...(onEventDetailsClick && { onEventDetailsClick })}
    />
  )
}

function renderPlanApprovalContent(
  event: ConversationTurnEntry,
  commonBubbleProps: CommonBubbleProps,
): ReactNode | null {
  const isPlanApproval =
    event.eventType === EventType.PLAN_APPROVAL_REQUEST &&
    event.notificationContext?.toolInput &&
    isExitPlanModeToolInput(event.notificationContext.toolInput)

  if (!isPlanApproval) return null

  const contextRenderer = selectPlanApprovalContextRenderer(event.notificationContext.toolInput, {
    enableCollapse: false,
  })
  return <NotificationBubble {...commonBubbleProps}>{contextRenderer}</NotificationBubble>
}

function renderToolPermissionContent(
  event: ConversationTurnEntry,
  commonBubbleProps: CommonBubbleProps,
  session: ClaudeSession,
): ReactNode | null {
  if (event.eventType !== EventType.TOOL_PERMISSION_REQUEST || !event.notificationContext) {
    return null
  }

  const { toolName, toolInput } = event.notificationContext

  if (
    isNotInteractable(session) &&
    toolName === ERRORED_TOOL_NAME &&
    event.notificationMessage?.trim()
  ) {
    return (
      <NotificationBubble {...commonBubbleProps}>
        <Typography>{event.notificationMessage}</Typography>
      </NotificationBubble>
    )
  }

  const contextRenderer = selectToolPermissionContextRenderer(toolName, toolInput, {
    enableCollapse: false,
  })
  return <NotificationBubble {...commonBubbleProps}>{contextRenderer}</NotificationBubble>
}

function resolveDialogTitle(eventType: EventType): string {
  switch (eventType) {
    case EventType.PLAN_APPROVAL_REQUEST:
      return 'Plan approval request'
    case EventType.TOOL_PERMISSION_REQUEST:
      return 'Tool permission request'
    case EventType.FILE_MODIFICATION:
      return 'Agent tool usage'
    case EventType.USER_PROMPT:
      return 'User reply'
    case EventType.AGENT_QUESTIONS:
      return 'Agent questions'
    case EventType.ERRORED_EVENT:
      return 'Error'
    case EventType.NON_INTERACTIVE_NOTIFICATION:
      return 'Notification'
    default:
      return 'Agent response'
  }
}
